import React from "react";
import styled from "styled-components";
import { useSortBy, useTable, useFlexLayout } from "react-table";
import PropTypes from "prop-types";
import { Col, Row, Table } from "reactstrap";
import { PaginationListStandalone } from "react-bootstrap-table2-paginator";
import SizePerPageDropdown from "../SizePerPageDropdown/SizePerPageDropdown";

const ReactSmartTable = ({
  loading,
  data,
  columns,
  onTableChange,
  pagination,
  isSearch,
  getCellProps,
  getFooterProps,
  withFooter,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: data.results,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useFlexLayout,
    useSortBy
  );

  const [page, setPage] = React.useState(1);
  const [sizePerPage, setSizePerPage] = React.useState(10);
  const [customHeaderGroups, setCustomHeaderGroups] = React.useState(null);
  const [search, setSearch] = React.useState(data?.search || "");
  const [tableHeight, setTableHeight] = React.useState();
  const tableRef = React.useRef();

  React.useEffect(() => {
    setPage(data.page);
    setSizePerPage(data.sizePerPage || data.size);
    return () => {
      setPage(1);
      setSizePerPage(10);
      setCustomHeaderGroups(null);
    };
  }, []);

  React.useEffect(() => {
    if (headerGroups[1] !== undefined) {
      let headersArray = {
        headers: [],
        getHeaderGroupProps: headerGroups[1]?.getHeaderGroupProps,
        getFooterGroupProps: headerGroups[1]?.getFooterGroupProps,
      };

      let subHeadersArray = {
        headers: [],
        getHeaderGroupProps: headerGroups[0].getHeaderGroupProps,
        getFooterGroupProps: headerGroups[0].getFooterGroupProps,
      };

      let headers = headerGroups[0];
      let subHeaders = headerGroups[1];

      subHeaders.headers.map(i => {
        if (i.depth > 0) {
          subHeadersArray.headers.push(i);
        } else {
          headersArray.headers.push(i);
        }
      });

      headers.headers.map(i => {
        if (i.depth !== undefined) {
          headersArray.headers.push(i);
        }
      });

      setCustomHeaderGroups([headersArray, subHeadersArray]);
    } else {
      let headersArray = {
        headers: [],
        getHeaderGroupProps: headerGroups[0].getHeaderGroupProps,
        getFooterGroupProps: headerGroups[0].getFooterGroupProps,
      };

      let headers = headerGroups[0];

      headers.headers.map(i => {
        headersArray.headers.push(i);
      });

      setCustomHeaderGroups([headersArray]);
    }
  }, [columns]);

  const paginationSettings = {
    alwaysShowAllBtns: true,
    custom: true,
    dataSize: parseInt(data.count),
    firstPageText: "<<",
    firstPageTitle: "First page",
    hidePageListOnlyOnePage: false,
    hideSizePerPage: false,
    lastPageText: ">>",
    lastPageTitle: "Last page",
    nextPageText: ">",
    nextPageTitle: "Next page",
    onPageChange: val => handleChangeTable("page", val),
    page: parseInt(data.page),
    pageStartIndex: 1,
    paginationSize: 5,
    prePageText: "<",
    prePageTitle: "Previous page",
    sizePerPage: sizePerPage,
    totalSize: data.count,
    withFirstAndLast: true,
  };

  const handleChangeTable = (type, value) => {
    let tableHeight = tableRef?.current?.clientHeight;
    setTableHeight(tableHeight || 0);

    const prev = {
      page,
      sizePerPage,
      search,
    };

    if (!isSearch) {
      delete prev.search;
    }

    if (type === "size") {
      setSizePerPage(value);
      onTableChange({
        ...prev,
        sizePerPage: value,
      });
      return void 0;
    }

    if (type === "page") {
      setPage(value);
      onTableChange({
        ...prev,
        page: value,
      });
      return void 0;
    }

    if (type === "search") {
      setSearch(value);
      onTableChange({
        ...prev,
        search: value,
      });
      return void 0;
    }
  };

  const staticCells = (i, l) => {
    switch (true) {
      case i === 0:
        return ["stickyHeadLeft", "stickyBodyLeft"];
        break;
      case i === l - 3:
        return ["fixedSumHead", "fixedSumBody"];
        break;
      case i === l - 2:
        return ["fixedPlanHead", "fixedPlanBody"];
        break;
      case i === l - 1:
        return ["fixedPerformanceHead", "fixedPerformanceBody"];
        break;
    }
  };

  const spinnerTable = (
    <div className="loader-for-react-table">
      <div className="spinner-border text-primary m-1" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      {!!isSearch && (
        <Row>
          <Col md={4}>
            <div className="search-box me-2 mb-2 d-inline-block">
              <div className="position-relative">
                <label htmlFor="search-bar-0" className="search-label">
                  <span id="search-bar-0-label" className="sr-only">
                    Search this table
                  </span>
                  <input
                    id="search-bar-0"
                    type="text"
                    aria-labelledby="search-bar-0-label"
                    className="form-control "
                    placeholder="Search"
                    value={search}
                    onChange={e => handleChangeTable("search", e.target.value)}
                  />
                </label>
                <i className="bx bx-search-alt search-icon" />
              </div>
            </div>
          </Col>
        </Row>
      )}
      {loading && (
        <div
          className="fade-in-custom-animate w-100 d-flex align-items-center justify-content-center spin-loader"
          style={{ height: tableHeight || "150px" }}
        >
          <div>{spinnerTable}</div>
        </div>
      )}
      {data.results.length > 0 ? (
        <Styles
          ref={tableRef}
          className={`fade-in-custom-animate position-relative ${
            loading && "d-none"
          }`}
        >
          {/* {loading } */}
          <div className="overflow-auto">
            <Table
              {...getTableProps()}
              className={`table table-hover sticky`}
              //table-striped
            >
              {customHeaderGroups !== null && (
                <>
                  <thead>
                    {customHeaderGroups.map((headerGroup, key) => (
                      <tr
                        {...headerGroup.getHeaderGroupProps()}
                        style={{
                          verticalAlign: "middle",
                        }}
                        key={key}
                      >
                        {headerGroup.headers.map((column, idx) => (
                          <th
                            {...column.getHeaderProps(
                              column.getSortByToggleProps()
                            )}
                            style={column.style}
                            key={idx}
                            className={`${
                              staticCells(
                                idx,
                                headerGroup.headers.length
                              )?.[0] || ""
                            } ${column.nowrap ? "text-nowrap" : ""}`}
                          >
                            {column.render("Header")}
                            <span>
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <i className="box bx bx-caret-down align-middle" />
                                ) : (
                                  <i className="box bx bx-caret-up align-middle" />
                                )
                              ) : (
                                ""
                              )}
                            </span>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {rows.map((row, i) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} key={i} style={row.style}>
                          {row.cells.map((cell, key) => {
                            return (
                              <td
                                {...cell.getCellProps([
                                  {
                                    className: cell.column.className,
                                    style: cell.column.style,
                                  },
                                  getCellProps(cell),
                                ])}
                                key={key}
                                className={
                                  staticCells(key, row.cells.length)?.[1] || ""
                                }
                              >
                                <span style={{ alignItems: "center" }}>
                                  {cell.render("Cell")}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  {withFooter && (
                    <tfoot>
                      {footerGroups.map((group, key) => (
                        <tr
                          key={key}
                          {...group.getFooterGroupProps()}
                          style={{ fontWeight: "550" }}
                        >
                          {group.headers.map((footer, idx) => (
                            <td
                              key={idx}
                              {...footer.getFooterProps([])}
                              style={{
                                verticalAlign: "middle",
                                textAlign: "center",
                                padding: 0,
                              }}
                              className={
                                staticCells(idx, group.headers.length)?.[1] ||
                                ""
                              }
                            >
                              {footer.render("Footer")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tfoot>
                  )}
                </>
              )}
            </Table>
          </div>
        </Styles>
      ) : (
        <div
          className={`fade-in-custom-animate w-100 ${
            loading ? "d-none" : "d-flex"
          } d-flex align-items-center justify-content-center spin-loader`}
          style={{
            height: "50px",
            fontWeight: 500,
          }}
        >
          <div>There is no data to display</div>
        </div>
      )}
      {pagination && (
        <Row className="align-items-md-center mt-30">
          <Col className="inner-custom-pagination d-flex">
            <div className="d-inline">
              <SizePerPageDropdown
                sizePerPage={data.sizePerPage || data.size}
                onChangeSize={handleChangeTable}
              />
            </div>

            <div className="text-md-right ms-auto">
              <div style={{ marginTop: "10px" }}>
                <PaginationListStandalone {...paginationSettings} />
              </div>
            </div>
          </Col>
        </Row>
      )}
    </React.Fragment>
  );
};

const Styles = styled.div`
  min-width: 300px;
  overflow: auto;

  .spin-loader {
    z-index: 2;
    background: rgba(255, 255, 255, 0.5);
    top: 0;
    bottom: 20px;
  }

  table {
    width: 100%;
    border-spacing: 0;
    border: 1px solid #eff2f7;
    --bs-table-hover-bg: rgba(229, 229, 229, 0.7);
    position: relative;

    .stickyHeadLeft {
      position: sticky !important;
      left: -1px;
      top: 0;
      z-index: 1;
      background-color: #2a3042;
    }

    .stickyBodyLeft {
      position: sticky !important;
      left: -1px;
      top: 0;
      z-index: 0;
      background-color: white;
    }

    .fixedPerformanceHead {
      position: sticky !important;
      right: 0;
      top: 0;
      z-index: 1;
      background-color: #2a3042;
    }

    .fixedPerformanceBody {
      position: sticky !important;
      right: 0;
      top: 0;
      z-index: 1;
      background-color: #2a3042;
    }

    .fixedPlanHead {
      position: sticky !important;
      right: 100px;
      top: 0;
      z-index: 1;
      background-color: #2a3042;
    }

    .fixedPlanBody {
      position: sticky !important;
      right: 100px;
      top: 0;
      z-index: 1;
      background-color: white;
    }

    .fixedSumHead {
      position: sticky !important;
      right: 175px;
      top: 0;
      z-index: 1;
      background-color: #2a3042;
    }

    .fixedSumBody {
      position: sticky !important;
      right: 175px;
      top: 0;
      z-index: 1;
      background-color: white;
    }

    .loader-for-react-table {
      position: absolute;
      left: 0;
      width: 100%;
      height: 100%;
      right: 0;
      bottom: 0;
      top: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(255, 255, 255, 0.3);
      z-index: 1;
    }

    thead {
      background: #2a3042;
      color: white;
    }

    tfoot {
      border-top: 1px solid #eff2f7;
      background: rgba(248, 249, 250, 1);
      color: black;
    }

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th {
      text-align: center;
      font-family: var(--bs-body-font-family);
      font-size: var(--bs-body-font-size);
      font-weight: 600;
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid #eff2f7;
      border-right: 1px solid #eff2f7;
    }

    td {
      transition-duration: 0.5s;
      text-align: left;
    }
  }
`;

ReactSmartTable.defaultProps = {
  columns: [
    {
      Header: "Test",
      accessor: "name",
      isSorted: true,
    },
  ],
  data: {
    results: [
      {
        id: 1,
        name: "Test",
      },
    ],
  },
  isSearch: false,
  withFooter: false,
  pagination: false,
};

ReactSmartTable.propTypes = {
  onTableChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  getCellProps: PropTypes.func.isRequired,
  data: PropTypes.object,
  columns: PropTypes.array,
  getFooterProps: PropTypes.func,
  pagination: PropTypes.bool,
  isSearch: PropTypes.bool,
  withFooter: PropTypes.bool,
};

export default ReactSmartTable;
